"use client";

import React from "react";
import { useCookieConsent } from "@/modules/hooks/common/useCookieConsent";
import { useClientTranslations } from "@/lib/translate";
import { Cookie, X } from "lucide-react";
import Link from "next/link";
import { routes } from "@/lib/utils/routes";

export default function CookieConsent() {
  const { showBanner, isInitialized, accept, decline, closeBanner } =
    useCookieConsent();
  const { t, i18n } = useClientTranslations("common");

  if (!isInitialized || !showBanner) {
    return null;
  }

  const isEn = typeof i18n?.language === "string" && i18n.language.startsWith("en");

  // Helper to ensure we never display raw translation keys if namespace is loading
  const resolveText = (key: string, fallback: string) => {
    try {
      const res = t(key, { defaultValue: fallback });
      if (!res || res === key) return fallback;
      return res;
    } catch {
      return fallback;
    }
  };

  const title = resolveText(
    "cookie_consent_title",
    isEn ? "Cookie & Privacy Settings" : "Cài đặt Cookie & Quyền riêng tư"
  );
  const description = resolveText(
    "cookie_consent_description",
    isEn
      ? "Together Work uses cookies to improve website performance, personalize user experience, and analyze site traffic."
      : "Together Work sử dụng cookie để cải thiện hiệu suất trang web, cá nhân hóa trải nghiệm và phân tích lưu lượng truy cập."
  );
  const acceptText = resolveText("cookie_consent_accept", isEn ? "Accept" : "Chấp nhận");
  const declineText = resolveText("cookie_consent_decline", isEn ? "Decline" : "Từ chối");
  const privacyText = resolveText("privacy_policy", isEn ? "Privacy Policy" : "Chính sách bảo mật");
  const closeText = resolveText("close", isEn ? "Close" : "Đóng");

  return (
    <aside
      role="region"
      aria-label={title}
      className="fixed bottom-5 right-5 left-5 sm:left-auto sm:max-w-md z-50 transition-all duration-300 ease-out"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-2xl border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-purple-100 dark:bg-purple-950/50 text-primary flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={closeBanner}
            aria-label={closeText}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1.5 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed mt-3 mb-4">
          {description}{" "}
          <Link
            href={routes.privacy}
            className="text-primary hover:underline font-medium inline-block"
          >
            {privacyText}
          </Link>
        </p>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2.5 pt-1">
          <button
            type="button"
            id="cookie-consent-decline-btn"
            onClick={decline}
            className="px-4 py-2 text-xs sm:text-sm font-medium rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors cursor-pointer"
          >
            {declineText}
          </button>
          <button
            type="button"
            id="cookie-consent-accept-btn"
            onClick={accept}
            className="px-5 py-2 text-xs sm:text-sm font-semibold rounded-xl bg-primary hover:bg-primaryDark text-white shadow-md shadow-purple-500/20 active:scale-[0.98] transition-all cursor-pointer"
          >
            {acceptText}
          </button>
        </div>
      </div>
    </aside>
  );
}
